import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const DEFAULT_MARKETS = [
  { location: 'Sacramento', enabled: true },
  { location: 'San Jose', enabled: true },
  { location: 'Los Angeles', enabled: false },
  { location: 'San Diego', enabled: false },
  { location: 'Oakland', enabled: false },
  { location: 'Fresno', enabled: false },
  { location: 'San Francisco', enabled: false },
  { location: 'Bakersfield', enabled: false },
  { location: 'Riverside', enabled: false },
  { location: 'Stockton', enabled: false },
  { location: 'Anaheim', enabled: false },
  { location: 'Roseville', enabled: false },
];

export default function AutoPilot() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [manualRunning, setManualRunning] = useState(false);
  const [newMarket, setNewMarket] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/api/autopilot/config');
      const cfg = res.data;
      if (!cfg.markets || cfg.markets.length === 0) cfg.markets = DEFAULT_MARKETS;
      setConfig(cfg);
      setRunning(cfg.running || false);
    } catch {
      setConfig({ enabled: false, markets: DEFAULT_MARKETS, leads_per_market: 10, interval_hours: 24, auto_enrich: false, min_score_alert: 80 });
    }
    setLoading(false);
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/api/autopilot/history');
      setHistory(res.data.history || []);
    } catch {}
  }, []);

  useEffect(() => { fetchConfig(); fetchHistory(); }, [fetchConfig, fetchHistory]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await api.post('/api/autopilot/config', config);
      setRunning(res.data.running || false);
      toast.success(config.enabled ? 'Auto-Pilot activated!' : 'Auto-Pilot configuration saved');
    } catch { toast.error('Failed to save config'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setManualRunning(true);
    try {
      const res = await api.post('/api/autopilot/run');
      toast.success(`Scan complete! ${res.data.total_found} leads found across ${res.data.market_results?.length || 0} markets`);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Manual scan failed');
    } finally { setManualRunning(false); }
  };

  const toggleMarket = (idx) => {
    setConfig(prev => {
      const markets = [...prev.markets];
      markets[idx] = { ...markets[idx], enabled: !markets[idx].enabled };
      return { ...prev, markets };
    });
  };

  const addMarket = () => {
    if (!newMarket.trim()) return;
    setConfig(prev => ({
      ...prev,
      markets: [...prev.markets, { location: newMarket.trim(), enabled: true }],
    }));
    setNewMarket('');
  };

  const removeMarket = (idx) => {
    setConfig(prev => ({
      ...prev,
      markets: prev.markets.filter((_, i) => i !== idx),
    }));
  };

  if (loading || !config) {
    return <div className="flex items-center justify-center h-64"><i className="fa-solid fa-spinner fa-spin text-primary text-xl mr-3" /><span>Loading Auto-Pilot...</span></div>;
  }

  const enabledMarkets = config.markets.filter(m => m.enabled).length;
  const estimatedLeads = enabledMarkets * config.leads_per_market;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="autopilot-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight flex items-center gap-3">
            Smart Auto-Pilot
            {running && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary animate-pulse-soft"><span className="w-2 h-2 bg-primary rounded-full" />Active</span>}
          </h1>
          <p className="text-text-muted text-sm mt-1">Automated daily permit scanning across your target markets</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runNow} disabled={manualRunning || enabledMarkets === 0} className="btn-secondary text-xs py-2" data-testid="run-now-btn">
            {manualRunning ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Scanning...</> : <><i className="fa-solid fa-play mr-1.5" />Run Now</>}
          </button>
          <button onClick={saveConfig} disabled={saving} className="btn-primary text-xs py-2" data-testid="save-autopilot-btn">
            {saving ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Saving...</> : <><i className="fa-solid fa-floppy-disk mr-1.5" />Save Config</>}
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: 'fa-location-dot', label: 'Active Markets', value: enabledMarkets, color: 'text-primary' },
          { icon: 'fa-users', label: 'Est. Leads/Cycle', value: estimatedLeads, color: 'text-secondary' },
          { icon: 'fa-clock', label: 'Scan Interval', value: `${config.interval_hours}h`, color: 'text-accent' },
          { icon: 'fa-history', label: 'Total Scans', value: history.length, color: 'text-text-primary' },
        ].map(s => (
          <div key={s.label} className="card-interactive p-4 text-center">
            <i className={`fa-solid ${s.icon} ${s.color} text-lg mb-2`} />
            <p className="text-xl font-bold text-text-primary">{s.value}</p>
            <p className="text-[10px] text-text-muted uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Config */}
        <div className="lg:col-span-2 space-y-6">
          {/* Enable Toggle */}
          <div className="card-interactive p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading text-base font-semibold text-text-primary">Auto-Pilot Engine</h3>
                <p className="text-xs text-text-muted mt-0.5">When enabled, scans run automatically at configured intervals</p>
              </div>
              <button
                onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative w-14 h-7 rounded-full transition-colors ${config.enabled ? 'bg-primary' : 'bg-border'}`}
                data-testid="autopilot-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${config.enabled ? 'translate-x-7' : ''}`} />
              </button>
            </div>

            {/* Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Leads Per Market</label>
                <input type="number" min={1} max={25} value={config.leads_per_market} onChange={e => setConfig(prev => ({ ...prev, leads_per_market: parseInt(e.target.value) || 10 }))} className="input-field" data-testid="leads-per-market" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Scan Interval (hours)</label>
                <select value={config.interval_hours} onChange={e => setConfig(prev => ({ ...prev, interval_hours: parseInt(e.target.value) }))} className="input-field" data-testid="interval-hours">
                  <option value={6}>Every 6 hours</option>
                  <option value={12}>Every 12 hours</option>
                  <option value={24}>Every 24 hours (Daily)</option>
                  <option value={48}>Every 48 hours</option>
                  <option value={168}>Weekly</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setConfig(prev => ({ ...prev, auto_enrich: !prev.auto_enrich }))}
                  className={`w-10 h-5 rounded-full transition-colors ${config.auto_enrich ? 'bg-primary' : 'bg-border'}`}
                  data-testid="auto-enrich-toggle"
                >
                  <span className={`block w-4 h-4 bg-white rounded-full shadow-sm transition-transform ml-0.5 ${config.auto_enrich ? 'translate-x-5' : ''}`} />
                </button>
                <div>
                  <p className="text-xs font-semibold text-text-primary">AI Auto-Enrich</p>
                  <p className="text-[10px] text-text-muted">Enrich top leads automatically</p>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Alert Score Threshold</label>
                <input type="number" min={0} max={100} value={config.min_score_alert} onChange={e => setConfig(prev => ({ ...prev, min_score_alert: parseInt(e.target.value) || 80 }))} className="input-field" data-testid="min-score-alert" />
              </div>
            </div>
          </div>

          {/* Target Markets */}
          <div className="card-interactive p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-base font-semibold text-text-primary flex items-center gap-2">
                <i className="fa-solid fa-bullseye text-primary text-sm" />Target Markets
              </h3>
              <span className="text-xs text-text-muted">{enabledMarkets} of {config.markets.length} active</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {config.markets.map((market, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${market.enabled ? 'border-primary/20 bg-primary/5' : 'border-border bg-surface'}`}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleMarket(i)} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${market.enabled ? 'bg-primary border-primary' : 'border-border'}`} data-testid={`toggle-market-${i}`}>
                      {market.enabled && <i className="fa-solid fa-check text-white text-[9px]" />}
                    </button>
                    <div>
                      <p className="text-sm font-semibold text-text-primary">{market.location}</p>
                    </div>
                  </div>
                  <button onClick={() => removeMarket(i)} className="p-1 text-text-muted hover:text-destructive transition-colors" data-testid={`remove-market-${i}`}>
                    <i className="fa-solid fa-xmark text-xs" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Market */}
            <div className="flex gap-2">
              <input
                type="text" value={newMarket} onChange={e => setNewMarket(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addMarket()}
                placeholder="Add market (city or zip)..." className="input-field flex-1"
                data-testid="add-market-input"
              />
              <button onClick={addMarket} className="btn-secondary text-xs py-2 px-4" data-testid="add-market-btn">
                <i className="fa-solid fa-plus mr-1" />Add
              </button>
            </div>
          </div>
        </div>

        {/* Scan History */}
        <div className="space-y-4">
          <div className="card-interactive p-5">
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
              <i className="fa-solid fa-history text-secondary text-xs" />Recent Scans
            </h3>
            {history.length === 0 ? (
              <div className="text-center py-6">
                <i className="fa-solid fa-clock-rotate-left text-2xl text-text-muted opacity-20 mb-2" />
                <p className="text-xs text-text-muted">No scan history yet</p>
                <p className="text-[10px] text-text-muted mt-0.5">Run your first scan to see results</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} className="p-3 rounded-xl bg-surface-alt border border-border/50">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-primary">{h.total_leads_found} leads</span>
                      <span className="text-[10px] text-text-muted">{new Date(h.completed_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-[10px] text-text-muted mb-1.5">{h.markets_scanned} markets scanned</p>
                    <div className="flex flex-wrap gap-1">
                      {(h.market_results || []).map((m, j) => (
                        <span key={j} className={`px-2 py-0.5 rounded text-[9px] font-medium ${m.status === 'success' ? 'bg-primary/10 text-primary' : 'bg-destructive-light text-destructive'}`}>
                          {m.location}: {m.leads_found}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* How It Works */}
          <div className="card-interactive p-5">
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
              <i className="fa-solid fa-circle-info text-accent text-xs" />How It Works
            </h3>
            <div className="space-y-3">
              {[
                { step: '1', title: 'Configure Markets', desc: 'Select cities/zip codes to scan' },
                { step: '2', title: 'Set Schedule', desc: 'Choose how often to scan' },
                { step: '3', title: 'Activate', desc: 'Toggle on and save' },
                { step: '4', title: 'Collect Leads', desc: 'Wake up to fresh leads daily' },
              ].map(s => (
                <div key={s.step} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">{s.step}</span>
                  <div>
                    <p className="text-xs font-semibold text-text-primary">{s.title}</p>
                    <p className="text-[10px] text-text-muted">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
